<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <form
      role="form"
      class="form-horizontal well"
      name="bulkBlockForm"
      @submit.prevent="handleSubmit(onSubmit)"
    >
      <div class="row">
        <Field
          :label="$t('BLOCK_NUMBERS_UPLOAD_INPUT')"
          :hide-label="false"
          :has-tooltip="true"
          :tooltip-text="$t('BLOCK_NUMBERS_UPLOAD_INPUT_TT')"
        >
          <template #element>
            <div class="col-sm-9 col-md-4">
              <FileReader
                :file-name="fileName"
                @load="parseFile"
              />
            </div>
            <label
              v-if="fileUploaded && validPhones.length > 0"
              class="form-group__label-required"
            >
              {{ $t('ITEMS_AMOUNT', {amount: totalItems}) }}
            </label>
            <label
              v-if="fileUploaded && validPhones.length == 0"
              v-t="'NO_VALID_PHONES_ERROR'"
              class="form-group__label-required vv-invalid"
            />
            <label
              v-if="submited && !fileUploaded"
              v-t="'NO_FILE'"
              class="form-group__label-required vv-invalid"
            />
          </template>
        </Field>
      </div>
      <div class="row">
        <Field
          :label="$t('BLOCK_NUMBERS_BLOCK_TYPE_INPUT')"
          :hide-label="false"
          :has-tooltip="true"
          :tooltip-text="$t('BLOCK_NUMBERS_BLOCK_TYPE_INPUT_TT')"
        >
          <template #element>
            <div class="col-sm-9 col-md-4">
              <MultipleToggle
                :value="selectedBlockType"
                :options="blockTypeOptions"
                @onToggle="blockTypeChange($event, 'selectedBlockType')"
              />
            </div>
          </template>
        </Field>
      </div>
      <div
        v-if="selectedBlockType === 1"
        class="row"
      >
        <Field
          :label="$t('BLOCK_NUMBERS_CAMPAGIN_ID_INPUT')"
          :hide-label="false"
          :has-tooltip="true"
          :tooltip-text="$t('BLOCK_NUMBERS_CAMPAGIN_ID_INPUT_TT')"
        >
          <template #element>
            <div class="col-sm-9 col-md-4">
              <SearchableDropdown
                v-model="campaignId"
                :config="dropdownConfig"
                class="m-0"
                rules="required"
              />
            </div>
          </template>
        </Field>
      </div>

      <div class="row">
        <div class="col-sm-offset-3 col-sm-3">
          <div
            class="number-to-block-list bs-callout bs-callout-sm bs-callout-success description"
          >
            <p>
              <b v-t="'VALID_NUMBERS'" />
              <span
                v-if="validPhones.length > 0"
                v-t="{path: 'ITEMS_AMOUNT', args: {amount: validPhones.length}}"
                class="text-muted"
              />
            </p>
            <ul class="list-unstyled">
              <li
                v-for="(number, index) in validPhones"
                :key="number + '-' + index"
              >
                {{ number }}
              </li>
            </ul>

            <p
              v-if="validPhones.length == 0"
              v-t="'NUMBERS_LIST_EMPTY'"
              class="text-center"
            />
          </div>
        </div>
        <div class="col-sm-3">
          <div
            class="number-to-block-list bs-callout bs-callout-sm bs-callout-danger description"
          >
            <p>
              <b v-t="'INVALID_NUMBERS'" />
              <span
                v-if="invalidPhones.length > 0"
                v-t="{path: 'ITEMS_AMOUNT', args: {amount: invalidPhones.length}}"
                class="text-muted"
              />
            </p>
            <ul class="list-unstyled">
              <li
                v-for="(number, index) in invalidPhones"
                :key="number + '-' + index"
              >
                {{ number }}
              </li>
            </ul>
            <p
              v-if="invalidPhones.length == 0"
              v-t="'NUMBERS_LIST_EMPTY'"
              class="text-center"
            />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-offset-2 col-md-offset-3 m-b-25">
          <button
            v-t="'BLOCK_NUMBERS_BUTTON_SAVE'"
            :disabled="isPending"
            name="rtb-form-save-button"
            class="btn btn-primary m-t-20 m-l-20"
            type="submit"
          />
        </div>
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import FileReader from '../BulkBlockedNumbers/FileReader.vue';
import Field from '@/core/components/LabelAndElement/LabelAndElement';
import MultipleToggle from '@/core/components/MultipleToggle/MultipleToggle';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';

import messages from '../../locales';

const campaignsConfig = {
  reduce: (option) => option.id,
  label: 'name',
};

export default {
  components: {
    FileReader,
    Field,
    MultipleToggle,
    SearchableDropdown,
  },
  props: {
    campaigns: {
      type: Array,
      default() {
        return [];
      },
    },
    actionStatus: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data: () => ({
    fileUploaded: false,
    blockTypeOptions: Object.freeze([
      { name: 'Global', value: 0 },
      { name: 'Campaign', value: 1 },
    ]),
    campaignId: '',
    submited: false,
    selectedBlockType: 0,
    validPhones: [],
    invalidPhones: [],
    fileName: '',
    totalItems: 0,
  }),
  computed: {
    dropdownConfig() {
      return {
        options: this.campaigns,
        ...campaignsConfig,
      };
    },
    isPending() {
      return this.actionStatus.status === 'pending';
    },
  },
  methods: {
    parseFile(file) {
      this.fileName = file.fileName;
      const phones = file.result
        .replace(/\r\n/g, ',')
        .replace(/\n/g, ',')
        .replace(/\r/g, ',')
        .split(',');
      this.totalItems = 0;
      this.validPhones = [];
      this.invalidPhones = [];
      for (var phone of phones) {
        this.totalItems++;
        phone = phone.trim();
        phone = phone[0] !== '+' ? '+' + phone : phone;
        if (this.validatePhoneForE164(phone)) {
          this.validPhones.push(phone);
        } else {
          this.invalidPhones.push(phone);
        }
      }
      this.fileUploaded = true;
    },
    blockTypeChange(event, blockTypeField) {
      this[blockTypeField] = event.value;
    },
    validatePhoneForE164(phoneNumber) {
      const phoneRegex = new RegExp(/^\+[1-9]\d{10,14}$/);
      return phoneRegex.test(phoneNumber);
    },
    onSubmit() {
      this.submited = true;
      const { validPhones, campaignId, selectedBlockType } = this;
      if (validPhones.length > 0) {
        if (selectedBlockType === 0) {
          this.$emit('bulk-block', { listToBlock: [...validPhones] });
        } else {
          this.$emit('bulk-block', {
            listToBlock: [...validPhones],
            campaignId,
          });
        }
        this.resetData();
      }
    },
    resetData() {
      this.selectedBlockType = 0;
      this.validPhones = [];
      this.invalidPhones = [];
      this.campaignId = '';
      this.fileUploaded = false;
      this.fileName = '';
      this.submited = false;
      this.totalItems = 0;
    },
  },
  i18n: {
    messages,
    locale: 'en',
  },
};
</script>

<style lang="scss">
.number-to-block-list {
  height: 250px;
  overflow-y: auto;
  border-top: 0;
  border-bottom: 0;
  border-right: 0;
  background: var(--reset-body-bg-color);
  border-bottom-right-radius: 10px;
  border-top-right-radius: 10px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
}


</style>